import React from 'react';
import { type Control } from 'react-hook-form';

import { ReactComponent as CloseIcon } from '@assets/svg/cross.svg';

import FacilityTreeBlock from '@components/common/FacilityTreeBlock/FacilityTreeBlock';
import DatePicker from '@components/form/DatePicker/DatePicker';
import Button from '@components/uikit/Button/Button';
import Switch from '@components/uikit/Switch/Switch';
import Tabs from '@components/uikit/Tabs/Tabs';

import { getAITypesList } from '@helpers/getAITypesList';

import useTranslation from '@hooks/useTranslation';

import { getUrgenciesList } from '@pages/SiteEngagement/helpers/getUrgenciesList';
import type { IEngagementStatsFacility } from '@pages/SiteEngagement/model/engagementStatsFacility';

import { useAssetTreeQuery } from '@services/useAssetTreeQuery';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { selectAppRtl } from '@store/useAppStore';

import {
  type IFormSiteEngagementMetricsFilters,
  SITE_ENGAGEMENT_METRICS_FILTERS_FIELDS,
} from '../../constants/schemaSiteEngagementMetricsFilters';
import style from './style.module.scss';

interface IProps {
  data?: IEngagementStatsFacility;
  control: Control<IFormSiteEngagementMetricsFilters>;
  isDirty: boolean;
  isValid: boolean;
  handleReset: () => void;
  handleSubmitForm: (e?: React.BaseSyntheticEvent) => Promise<void>;
  handleCloseFilter: () => void;
}

const SiteEngagementMetricsFilters: React.FC<IProps> = ({
  data,
  control,
  isDirty,
  isValid,
  handleReset,
  handleSubmitForm,
  handleCloseFilter,
}) => {
  const { t } = useTranslation();

  const { data: profileData } = useUserProfileQuery();
  const { data: assetTreeData } = useAssetTreeQuery();

  const rtl = selectAppRtl();

  const typesList = getAITypesList(t);
  const viewByList = [
    { value: 'days', label: t('Days') },
    { value: 'hours', label: t('Hours') },
  ];
  const urgenciesList = getUrgenciesList(data?.filter_data.urgencies || [], t);

  const { filter_data } = data || {};
  const filterLimits = {
    minTimeCreated: new Date(filter_data?.min_time_created ?? Date.now()),
    maxTimeCreated: new Date(filter_data?.max_time_created ?? Date.now()),
  };

  const facilityStructureData = assetTreeData?.facilities || [];

  return (
    <div className={style.siteEngagementMetricsFiltersWrapper}>
      <div className={style.siteEngagementMetricsFiltersHead}>
        <span className={style.siteEngagementMetricsFiltersHeadText}>{t('Filters')}</span>
        <CloseIcon className={style.closeIcon} onClick={handleCloseFilter} />
      </div>
      <div className={style.siteEngagementMetricsFiltersBody}>
        <div className={style.filterFields}>
          <div className={style.filterFieldContainer}>
            <Tabs
              label={t('Action Item Type')}
              id="type"
              name={SITE_ENGAGEMENT_METRICS_FILTERS_FIELDS.TYPE}
              control={control}
              tabsItems={typesList}
              type="woHelperText"
            />
          </div>
          <div className={style.filterFieldContainer}>
            <Tabs
              label={t('Urgency')}
              id="urgency"
              name={SITE_ENGAGEMENT_METRICS_FILTERS_FIELDS.URGENCY}
              control={control}
              tabsItems={urgenciesList}
              type="woHelperText"
            />
          </div>
          <div className={style.filterFieldContainer}>
            <Tabs
              label={t('View By')}
              id="metric_type"
              name={SITE_ENGAGEMENT_METRICS_FILTERS_FIELDS.METRIC_TYPE}
              control={control}
              tabsItems={viewByList}
              type="woHelperText"
            />
          </div>
          <div className={style.filterFieldContainer}>
            <DatePicker
              id="daterange-engagement-metrics"
              label={t('Action Item Update Date Range')}
              placeholderText={t('Select Date Range')}
              dateFormat={rtl ? 'yyyy/MM/dd' : `${profileData?.dateFormat.date}`}
              control={control}
              name={SITE_ENGAGEMENT_METRICS_FILTERS_FIELDS.TIME_CREATED_RANGE}
              minDate={filterLimits.minTimeCreated}
              maxDate={filterLimits.maxTimeCreated}
              selectsRange
              showMonthDropdown
              showYearDropdown
              autoComplete="off"
              type="woHelperText"
            />
          </div>
          <div className={style.filterFieldContainer}>
            <Switch
              label={t('Customer - Reported Failures')}
              id="reported_failure"
              name={SITE_ENGAGEMENT_METRICS_FILTERS_FIELDS.REPORTED_FAILURE}
              control={control}
              type="woHelperText"
            />
          </div>
          <div className={style.facilityTreeBlockContainer}>
            <FacilityTreeBlock
              facilityStructureData={facilityStructureData}
              control={control}
              locationsFormName={SITE_ENGAGEMENT_METRICS_FILTERS_FIELDS.FACILITIES}
              variant="siteEngagementMetricsFilters"
              showSearch={true}
              showSelectAll={true}
              expanded={false}
            />
          </div>
        </div>
      </div>
      <div className={style.siteEngagementMetricsFiltersFooter}>
        <div className={style.leftSideFooter}>
          {isDirty && <Button type="secondary" text={t('Reset all filters')} onClick={handleReset} />}
        </div>
        <div className={style.rightSideFooter}>
          <Button type="secondary" text={t('Cancel')} onClick={handleCloseFilter} />
          <Button
            type="primary"
            variant="small"
            text={t('Apply')}
            onClick={handleSubmitForm}
            disabled={!isValid}
          />
        </div>
      </div>
    </div>
  );
};

export default SiteEngagementMetricsFilters;
